import React from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Badge,
  Button,
} from '@mui/material';
import {
  Highlight as HighlightIcon,
  Comment as CommentIcon,
  PanTool as PanToolIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Gavel as ComplianceIcon,
  AccountTree as RoutingIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

interface AnnotationToolbarProps {
  annotationMode: string;
  onModeChange: (mode: string) => void;
  onRefresh: () => void;
  onExport: () => void;
  onToggleAnnotations: () => void;
  onToggleCompliance: () => void;
  onToggleRouting: () => void;
  onStartCollaboration?: () => void;
  showAnnotations: boolean;
  enableCollaboration?: boolean;
}

const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  annotationMode,
  onModeChange,
  onRefresh,
  onExport,
  onToggleAnnotations,
  onToggleCompliance,
  onToggleRouting,
  onStartCollaboration,
  showAnnotations,
  enableCollaboration = false,
}) => {
  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: string | null) => {
    if (newMode !== null) {
      onModeChange(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Annotation Mode Selection */}
      <ToggleButtonGroup
        value={annotationMode}
        exclusive
        onChange={handleModeChange}
        size="small"
        aria-label="annotation mode"
      >
        <ToggleButton value="view" aria-label="view mode">
          <Tooltip title="View Mode">
            <PanToolIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="highlight" aria-label="highlight mode">
          <Tooltip title="Highlight Mode">
            <HighlightIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="comment" aria-label="comment mode">
          <Tooltip title="Comment Mode">
            <CommentIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* View Controls */}
      <Tooltip title={showAnnotations ? "Hide Annotations" : "Show Annotations"}>
        <IconButton onClick={onToggleAnnotations} color={showAnnotations ? "primary" : "default"}>
          {showAnnotations ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Show Compliance Report">
        <IconButton onClick={onToggleCompliance}>
          <ComplianceIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Show Routing Decision">
        <IconButton onClick={onToggleRouting}>
          <RoutingIcon />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Actions */}
      <Tooltip title="Refresh Annotations">
        <IconButton onClick={onRefresh}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Export Annotations (Coming Soon)">
        <IconButton onClick={onExport} disabled>
          <DownloadIcon />
        </IconButton>
      </Tooltip>

      {/* Collaboration */}
      {enableCollaboration && (
        <>
          <Divider orientation="vertical" flexItem />
          <Button
            size="small"
            variant="outlined"
            startIcon={<GroupIcon />}
            onClick={onStartCollaboration}
          >
            Start Collaboration
          </Button>
        </>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Save Button */}
      <Button size="small" variant="contained" startIcon={<SaveIcon />}>
        Save Changes
      </Button>
    </Box>
  );
};

export default AnnotationToolbar;